import { StyleEntry } from 'Shared/backend/styleUtils';
import { Borders } from 'Shared/backend/styles/Borders';
import { Margins } from 'Shared/backend/styles/Margins';
import { Padding } from 'Shared/backend/styles/Padding';
import { Brightness } from 'Shared/themes/Brightness';
import { Colour } from 'Shared/themes/Colour';
import { FontWeight } from 'Shared/themes/FontWeight';
import { FontSize } from 'Shared/themes/FontSize';
import { LetterSpacing } from 'Shared/themes/LetterSpacing';
import { TextShadow, textShadowInitial } from 'Shared/themes/TextShadow';
import { FontFamily } from 'Shared/themes/FontFamily';
import { TextTransform } from 'Shared/themes/TextTransform';
import { TextDecoration } from 'Shared/themes/TextDecoration';
import { Width } from 'Shared/themes/Width';
import { ScrollbarColour } from 'Shared/themes/ScrollbarColour';
import { GridGap } from 'Shared/themes/GridGap';
import { BannerLogo } from 'Shared/themes/BannerLogo';
import { MenuTitleColour } from 'Shared/themes/MenuTitleColour';
import { MenuButtonColours } from 'Shared/themes/MenuButtonColours';
import { palette } from 'Shared/frontend/SettingsProvider';
import { colourRule, resolveColour, resolvedBorder } from 'Shared/model/settingColour';
import { resolveFontSize } from 'Shared/model/settingText';
import { escapeStyleClass, pathLocation } from 'Shared/frontend/widgets/locationMatches';
import { LocationPathStep } from 'Shared/model/venueSettings';


export function tweaksStyle(sizes,text): {[name: string]: StyleEntry}
{
	return {
		Brightness: {
			explanation: 'Change the brightness.',
			ui: Brightness,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => ({
					filter: `brightness(${params.brightness}%)`
				}),
			}]
		},

		Colour: {
			explanation: 'Set the foreground colour.',
			//explanation: 'Set the foreground colour. Note this property is automatically inherited in CSS.',  XXX in practice title styling is often likely to override
			ui: Colour,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => colourRule('color', params.colour, palette()),
			}]
		},

		'Background colour': {
			explanation: 'Set the background colour.',
			ui: Colour,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => colourRule('background', params.colour, palette()),
			}]
		},

		'Centre object': {
			explanation: 'Centers the contents vertically and horizontally within its parent.',

			rules: [{
				location: () => ['*'],
				css: () => ({
					height: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				})
			}]
		},

		//XXX use in The Townie club page description. Should descriptions be left justified by default?
		'Left align': {
			explanation: 'Aligns the text to the left. Note children inherit text aligment by default.',

			rules: [{
				location: () => ['*'],
				css: () => ({
					textAlign: 'left'
				})
			}]
		},

		'Margins': {
			explanation: 'Add margins.',

			ui: Margins,

			initial: {
				margin: { sides: 'different' }
			},

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				margin: (params = {}) => params?.margin,
			}]
		},

		'Padding': {
			explanation: 'Add padding to the children.',

			ui: Padding,

			initial: {
				padding: { sides: 'different' }
			},

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				padding: (params = {}) => params?.padding,
			}]
		},

		'Borders': {
			explanation: 'Add borders to the children.',

			ui: Borders,

			initial: {
				border: { sides: 'all',all:{ style:'solid',colour:'gray',width:'2px' }}
			},

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				/* Resolved on the way out, as the Style Builder's own Borders property does: the two
				   share the colour control, so a border here can name a palette entry too. */
				border: (params = {}) => resolvedBorder(params?.border,palette()),
			}]
		},

		'Font weight': {
			explanation: 'Set how bold the text is.',
			ui: FontWeight,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => params.weight ? {fontWeight: params.weight} : {},
			}]
		},

		'Font size': {
			explanation: 'Set the text size - a fixed size or one that scales with the screen.',
			ui: FontSize,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => params.size ? {fontSize: resolveFontSize(params.size)} : {},
			}]
		},

		'Letter spacing': {
			explanation: 'Add or remove space between letters (in px).',
			ui: LetterSpacing,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => params.spacing != undefined ? {letterSpacing: params.spacing} : {},
			}]
		},

		'Text shadow': {
			explanation: 'Add a drop shadow or glow behind the text.',
			ui: TextShadow,

			initial: textShadowInitial,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => textShadowRule(params),
			}]
		},

		'Font family': {
			explanation: 'Set the font, eg \'"Bodoni Moda", serif\'.',
			ui: FontFamily,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => params.family ? {fontFamily: params.family} : {},
			}]
		},

		'Text transform': {
			explanation: 'Force upper or lower case.',
			ui: TextTransform,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => params.transform ? {textTransform: params.transform} : {},
			}]
		},

		'Text decoration': {
			explanation: 'Underline or strike through the text.',
			ui: TextDecoration,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => params.decoration ? {textDecoration: params.decoration} : {},
			}]
		},

		'Width': {
			explanation: 'Make the element span the full width of its container.',
			ui: Width,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => params.width == 'full' ? {width: '100%'} : {},
			}]
		},

		'Scrollbar colour': {
			explanation: 'Colour the scrollbar of a sideways-scrolling row of posters or menu items.',
			ui: ScrollbarColour,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => scrollbarColourRule(params.handle, params.track),
			}]
		},

		'Grid gap': {
			explanation: 'Set the column and row gaps between the cells of a grid.',
			ui: GridGap,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => gridGapRule(params.columnGap, params.rowGap),
			}]
		},

		'Banner logo': {
			explanation: 'Set the fluid side padding and height of a home-page banner logo image.',
			ui: BannerLogo,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => bannerLogoRule(params.padding, params.height),
			}]
		},

		'Menu title colour': {
			explanation: 'Colour the section headings and item names of a menu.',
			ui: MenuTitleColour,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => menuTitleColourRule(params.colour),
			}]
		},

		'Menu button colours': {
			explanation: `Colour the Food and Drinks buttons of a menu's sticky button row.`,
			ui: MenuButtonColours,

			rules: [{
				location: (params = {}) =>
					selectLocation(params?.selector,params?.selectorClass,params?.selectorPath),
				css: (params = {}) => menuButtonColoursRule(params.food, params.drinks),
			}]
		},
	};
}


/*
	The colour travels down as a custom property rather than as a plain 'color' declaration. Nested
	sections generate selectors of equal specificity, so which one won would otherwise depend on the
	order Emotion happened to insert them in. Custom properties inherit, so the innermost section
	always wins.
*/
function menuTitleColourRule(colour:any): object
{
	const resolved = resolveColour(colour, palette());
	if (resolved == '')
		return {};

	return {
		'--menu-title-colour': resolved,
		'.title': {
			color: 'var(--menu-title-colour)'
		}
	};
}


/* The Food / Drinks button backgrounds, each dropped when unset. A Plain button is cleared back to
   whatever sits behind the row: it is the 'no colour' one of the three, and the sub-nav button
   styling underneath would otherwise fill it like any other. */
function menuButtonColoursRule(food:any, drinks:any): object
{
	const resolvedFood = resolveColour(food, palette());
	const resolvedDrinks = resolveColour(drinks, palette());

	if (resolvedFood == '' && resolvedDrinks == '')
		return {};

	const ret: any = {'button.plain': {backgroundColor: 'inherit'}};
	if (resolvedFood != '')
		ret['button.food'] = {backgroundColor: resolvedFood};
	if (resolvedDrinks != '')
		ret['button.drinks'] = {backgroundColor: resolvedDrinks};
	return ret;
}


/* 'scrollbar-color: <handle> <track>', or {} when neither is set. A half-set pair falls back to the
   Content.tsx scrollbar colours for the missing side, since 'scrollbar-color' can't mix a colour
   with 'auto'. */
function scrollbarColourRule(handle:any, track:any): object
{
	const resolvedHandle = resolveColour(handle, palette());
	const resolvedTrack = resolveColour(track, palette());

	if (resolvedHandle == '' && resolvedTrack == '')
		return {};

	return {scrollbarColor: `${resolvedHandle || 'black'} ${resolvedTrack || '#666363'} !important`};
}


/* The column-gap / row-gap pair, each dropped when its box is empty. '0' is a real value, so test
   for an empty string rather than falsiness. */
function gridGapRule(columnGap:any, rowGap:any): object
{
	const ret: any = {};
	if (columnGap != undefined && columnGap != '')
		ret.columnGap = columnGap;
	if (rowGap != undefined && rowGap != '')
		ret.rowGap = rowGap;
	return ret;
}


/* The banner logo's left/right padding and height, each dropped when unset so a rule that sets only
   one still works. resolveFontSize() turns the Amount control's fixed-or-fluid value into a css
   length, the same way the 'Font size' style does. */
function bannerLogoRule(padding:any, height:any): object
{
	const ret: any = {};
	if (padding != undefined && padding != '') {
		const value = resolveFontSize(padding);
		ret.paddingLeft = value;
		ret.paddingRight = value;
	}
	if (height != undefined && height != '')
		ret.height = height;
	return ret;
}


/* '0 0 5px black' from the four stored parts, or {} when nothing has been set. A number renders as
   px; the colour resolves a palette link or literal, falling back to black. */
function textShadowRule(params:any): object
{
	if (params.x == undefined && params.y == undefined && params.blur == undefined && !params.colour)
		return {};

	const colour = resolveColour(params.colour, palette()) || 'black';
	return {textShadow: `${params.x ?? 0}px ${params.y ?? 0}px ${params.blur ?? 0}px ${colour}`};
}

/*
	Where a style that lets the venue point it applies - see Selector.tsx, which writes both params.
	A style tag written before the control existed carries no selector at all, and reads as 'self'.

	'class' matches anything at or below the styled component carrying the class. Selector.tsx picks
	the class in the same click as the selector, so the missing-class case is corrupt data rather
	than an unfinished choice - it falls back to 'self', an empty class filter matching every
	component rather than none.

	'path' is the general case, spelled out step by step by the venue rather than picked whole - see
	pathLocation(), which builds it. A path with no steps is the styled component itself.
*/
function selectLocation(selector:'self'|'class'|'path'|undefined,
	aClass:string|undefined, path:LocationPathStep[]|undefined)
{
	if (!selector)
		return ['*'];

	switch (selector) {
		case 'self': return ['*'];
		case 'class': return aClass ? ['**',`*:${escapeStyleClass(aClass)}`] : ['*'];
		case 'path': return pathLocation(path ?? []);
	}
}

